import { useEffect, useState } from "react";
import { toast } from "sonner";
import { X } from "lucide-react";
import { http, formatApiError } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";

export const NonBillableField = ({ v, setV }) => {
  const { user } = useAuth();
  const can = user?.role === "admin" || Array.isArray(user?.site_ids);
  return (
    <div className="border border-slate-200 rounded-md p-3 space-y-2" data-testid="vacation-nb-block">
      <label className="flex items-start gap-2 text-sm cursor-pointer">
        <input type="checkbox" disabled={!can} className="mt-0.5 h-4 w-4" checked={!!v.non_billable} onChange={(e) => setV({ ...v, non_billable: e.target.checked })} data-testid="vacation-nb" />
        <span><b>Non facturable au client</b> <span className="text-xs text-slate-500">— la vacation reste payée à l'agent mais n'apparaît pas dans les devis et factures générés depuis le planning{!can && " (réservé à l'administrateur et aux chefs de site)"}</span></span>
      </label>
      {v.non_billable && <input className="input" maxLength={200} placeholder="Motif obligatoire (ex. formation, doublure, geste commercial)" value={v.non_billable_reason || ""} disabled={!can}
        onChange={(e) => setV({ ...v, non_billable_reason: e.target.value })} data-testid="vacation-nb-reason" />}
      {v.non_billable && v.non_billable_by && <p className="text-[11px] text-slate-500" data-testid="vacation-nb-trace">Marquée par {v.non_billable_by}{v.non_billable_at ? ` le ${new Date(v.non_billable_at).toLocaleString("fr-FR", { dateStyle: "short", timeStyle: "short" })}` : ""}</p>}
    </div>
  );
};

export const NonBillableDialog = ({ siteId, siteName, defaultStart, defaultEnd, onClose, onDone }) => {
  const [f, setF] = useState({ start: defaultStart, end: defaultEnd, non_billable: true, reason: "" });
  const [count, setCount] = useState(null);
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const body = { site_id: siteId, start: f.start, end: f.end, non_billable: f.non_billable, reason: f.reason };
  useEffect(() => {
    let live = true;
    setCount(null); setErr("");
    if (!f.start || !f.end) return undefined;
    http.post("/vacations/non-billable", { ...body, reason: "dry-run", dry_run: true }).then((r) => live && setCount(r.data.count)).catch((e) => live && setErr(formatApiError(e)));
    return () => { live = false; };
  }, [f.start, f.end, f.non_billable]); // eslint-disable-line react-hooks/exhaustive-deps
  const apply = async () => {
    if (f.non_billable && f.reason.trim().length < 3) { setErr("Indiquez le motif."); return; }
    setBusy(true); setErr("");
    try { const r = await http.post("/vacations/non-billable", body); toast.success(`${r.data.count} vacation(s) mise(s) à jour`); onDone(); onClose(); }
    catch (e) { setErr(formatApiError(e)); } finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 z-50 bg-slate-900/50 grid place-items-center p-4" data-testid="nb-dialog">
      <div className="bg-white rounded-lg w-full max-w-md p-5 space-y-3">
        <div className="flex items-center"><h2 className="font-display font-bold text-lg">Facturation des vacations — {siteName}</h2>
          <button type="button" onClick={onClose} className="ml-auto p-1 rounded hover:bg-slate-100" data-testid="nb-close"><X className="h-4 w-4" /></button></div>
        <div className="flex gap-2">
          {[[true, "Rendre non facturables"], [false, "Rendre de nouveau facturables"]].map(([k, l]) => (
            <button key={String(k)} type="button" onClick={() => setF({ ...f, non_billable: k })} className={`flex-1 text-sm font-semibold rounded-md border px-2 py-2 ${f.non_billable === k ? "bg-slate-900 text-white border-slate-900" : "border-slate-300"}`} data-testid={`nb-mode-${k ? "on" : "off"}`}>{l}</button>
          ))}
        </div>
        <div className="flex gap-2">
          <label className="text-xs font-semibold text-slate-600 flex-1">Du<input type="date" className="input mt-1" value={f.start} onChange={(e) => setF({ ...f, start: e.target.value })} data-testid="nb-start" /></label>
          <label className="text-xs font-semibold text-slate-600 flex-1">Au<input type="date" className="input mt-1" value={f.end} onChange={(e) => setF({ ...f, end: e.target.value })} data-testid="nb-end" /></label>
        </div>
        {f.non_billable && <input className="input" maxLength={200} placeholder="Motif obligatoire (ex. formation, doublure, geste commercial)" value={f.reason} onChange={(e) => setF({ ...f, reason: e.target.value })} data-testid="nb-reason" />}
        <p className="text-sm" data-testid="nb-count">{count === null ? "Calcul…" : `${count} vacation(s) concernée(s) sur ce site`}</p>
        <p className="text-[11px] text-slate-500">Les heures et la paie des agents ne changent pas. Seuls les devis et factures générés depuis le planning excluent ces vacations.</p>
        {err && <p className="text-sm text-rose-700" data-testid="nb-error">{err}</p>}
        <div className="flex justify-end gap-2">
          <button type="button" onClick={onClose} className="px-3 py-2 text-sm rounded-md border border-slate-300">Annuler</button>
          <button type="button" disabled={busy || !count} onClick={apply} className="px-3 py-2 text-sm font-semibold rounded-md bg-[#0B2A7A] text-white disabled:opacity-50" data-testid="nb-apply">Appliquer</button>
        </div>
      </div>
    </div>
  );
};
